// src/components/FiltroPonteImobiliaria.jsx
import React from 'react';
import { RefreshCw, Search } from 'lucide-react';

const estadosBrasil = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

const tiposDestino = [
  'Casa',
  'Apartamento',
  'Apartamento na planta',
  'Kitnet',
  'Terreno / Área',
  'Sala comercial',
  'Ponto comercial',
  'Galpão / Barracão',
  'Empreendimentos',
  'Chácara',
  'Fazenda',
  'Indiferente',
];

const condicoes = [
  { value: 'sim', label: 'Sim' },
  { value: 'nao', label: 'Não' },
  { value: 'a_negociar', label: 'A negociar' },
];

export default function FiltroPonteImobiliaria({
  filtros,
  setFiltros,
  onBuscar,
  onLimpar,
  loading = false,
}) {
  const handleChange = (campo, valor) => {
    setFiltros((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const handleEnter = (e) => {
    if (e.key === 'Enter') {
      onBuscar?.();
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6 shadow-sm">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="text-base font-bold text-gray-900">
            Filtros da Ponte
          </h2>

          <p className="text-sm text-gray-500">
            Busque oportunidades por cidade de origem, cidade de destino, tipo
            desejado ou condição de negociação.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onLimpar}
            className="px-4 py-2 rounded-xl border border-gray-300 text-sm font-semibold hover:bg-gray-50"
          >
            Limpar
          </button>

          <button
            type="button"
            onClick={onBuscar}
            disabled={loading}
            className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Buscando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-2">
          <label className="label">Buscar</label>

          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />

            <input
              type="text"
              value={filtros.termo || ''}
              onChange={(e) => handleChange('termo', e.target.value)}
              onKeyDown={handleEnter}
              placeholder="Código, bairro, cidade, rota..."
              className="input pl-9"
            />
          </div>
        </div>

        <div>
          <label className="label">Estado de origem</label>

          <select
            value={filtros.estadoOrigem || ''}
            onChange={(e) => handleChange('estadoOrigem', e.target.value)}
            className="input"
          >
            <option value="">Todos</option>
            {estadosBrasil.map((uf) => (
              <option key={uf} value={uf}>
                {uf}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">Cidade de origem</label>

          <input
            type="text"
            value={filtros.cidadeOrigem || ''}
            onChange={(e) => handleChange('cidadeOrigem', e.target.value)}
            onKeyDown={handleEnter}
            placeholder="Ex: Cuiabá"
            className="input"
          />
        </div>

        <div>
          <label className="label">Estado de destino</label>

          <select
            value={filtros.estadoDestino || ''}
            onChange={(e) => handleChange('estadoDestino', e.target.value)}
            className="input"
          >
            <option value="">Todos</option>
            {estadosBrasil.map((uf) => (
              <option key={uf} value={uf}>
                {uf}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">Cidade de destino</label>

          <input
            type="text"
            value={filtros.cidadeDestino || ''}
            onChange={(e) => handleChange('cidadeDestino', e.target.value)}
            onKeyDown={handleEnter}
            placeholder="Ex: Sinop"
            className="input"
          />
        </div>

        <div>
          <label className="label">Tipo desejado no destino</label>

          <select
            value={filtros.tipoDesejadoDestino || ''}
            onChange={(e) =>
              handleChange('tipoDesejadoDestino', e.target.value)
            }
            className="input"
          >
            <option value="">Todos</option>
            {tiposDestino.map((tipo) => (
              <option key={tipo} value={tipo}>
                {tipo}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">Condição de negociação</label>

          <select
            value={filtros.condicaoNegociacao || ''}
            onChange={(e) => handleChange('condicaoNegociacao', e.target.value)}
            className="input"
          >
            <option value="">Todas</option>
            {condicoes.map((condicao) => (
              <option key={condicao.value} value={condicao.value}>
                {condicao.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4 rounded-xl bg-blue-50 border border-blue-100 p-4 text-sm text-blue-900">
        <strong>Como usar:</strong> a Ponte Imobiliária trabalha com rota de
        mudança. Você pode procurar imóveis de uma cidade de origem para uma
        cidade de destino, ou visualizar oportunidades de clientes que estão
        chegando ou saindo da sua cidade de atuação.
      </div>

      <style jsx>{`
        .input {
          @apply block w-full p-3 border border-gray-300 rounded-xl text-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500 bg-white;
        }

        .label {
          @apply block text-sm font-medium text-gray-600 mb-2;
        }
      `}</style>
    </div>
  );
}
